import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Search, SlidersHorizontal, ShoppingCart } from 'lucide-react';
import { catalogAPI, miscAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import AuthGate from '../AuthGate';
import Breadcrumbs from '../layout/Breadcrumbs';
import { toast } from 'sonner';

export default function ShopPage({ mode = 'shop' }) {
  return <ShopPageContent mode={mode} />;
}

function ShopPageContent({ mode }) {
  const isPrebook = mode === 'prebook';
  const pageTitle = isPrebook ? 'Prebook Orders' : 'Our Varieties';
  const basePath = isPrebook ? '/prebook' : '/shop';
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [catFilterText, setCatFilterText] = useState('');
  const [colorFilterText, setColorFilterText] = useState('');
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Modal state
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const currentCategory = searchParams.get('category') || '';
  const currentSearch = searchParams.get('search') || '';
  const currentSort = searchParams.get('sort') || 'featured';
  const currentPage = parseInt(searchParams.get('page') || '1');
  const isWeeklySpecial = searchParams.get('weekly_special') === 'true';
  const isPrebookFilter = searchParams.get('prebook') === 'true';
  const currentColor = searchParams.get('color') || '';

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage, limit: 24, sort: currentSort };
      if (currentCategory) params.category = currentCategory;
      if (currentSearch) params.search = currentSearch;
      if (isWeeklySpecial) params.is_weekly_special = true;
      if (isPrebookFilter) params.is_prebook_eligible = true;
      if (currentColor) params.color = currentColor;
      const res = await catalogAPI.getProducts(params);
      setProducts(res.data.products);
      setTotal(res.data.total);
    } catch (err) {
      console.error('Failed to load products', err);
    } finally {
      setLoading(false);
    }
  }, [currentCategory, currentSearch, currentSort, currentPage, isWeeklySpecial, isPrebookFilter, currentColor]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await catalogAPI.getCategories();
        setCategories(res.data.categories);
      } catch { }
    };
    loadCategories();
  }, []);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) { params.set(key, value); } else { params.delete(key); }
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  const clearFilters = () => { setSearchParams({}); };

  const [colors, setColors] = useState([]);
  useEffect(() => {
    miscAPI.getColors().then(r => setColors(r.data.colors)).catch(() => {});
  }, []);
  const hasActiveFilters = currentCategory || currentSearch || isWeeklySpecial || isPrebookFilter || currentColor;

  const breadcrumbs = [{ label: pageTitle, href: basePath }];
  if (currentCategory) breadcrumbs.push({ label: currentCategory });
  if (currentSearch) breadcrumbs.push({ label: `Search: "${currentSearch}"` });

  // Modal navigation
  const openModal = (item, index) => { setSelectedItem(item); setSelectedIndex(index); };
  const closeModal = useCallback(() => { setSelectedItem(null); setSelectedIndex(-1); }, []);
  const goNext = useCallback(() => {
    if (selectedIndex < products.length - 1) {
      setSelectedIndex(selectedIndex + 1);
      setSelectedItem(products[selectedIndex + 1]);
    }
  }, [selectedIndex, products]);
  const goPrev = useCallback(() => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
      setSelectedItem(products[selectedIndex - 1]);
    }
  }, [selectedIndex, products]);

  // Keyboard nav
  useEffect(() => {
    if (!selectedItem) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedItem, closeModal, goNext, goPrev]);

  const getMinPrice = (product) => {
    if (!product.tier_pricing || product.tier_pricing.length === 0) return 0;
    return Math.min(...product.tier_pricing.map(t => t.price));
  };

  return (
    <div className="pw-container py-2">
      <Breadcrumbs items={breadcrumbs} />

      <div className="flex gap-8">
        {/* Sidebar Filters - Desktop */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-28">
            <h3 className="text-sm font-semibold tracking-wider uppercase mb-4">Filters</h3>

            {/* Search */}
            <div className="mb-5">
              <label className="text-[10px] font-semibold tracking-wider uppercase text-[#6f6a63] block mb-2">Search</label>
              <div className="relative">
                <input
                  data-testid="shop-search-input"
                  type="text"
                  value={currentSearch}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-8 pr-3 py-2 border border-[#e7e3dd] rounded-lg text-xs bg-white focus:outline-none focus:border-[#2f6b4f]"
                />
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6f6a63]" />
              </div>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Category</h4>
              <input
                type="text"
                value={catFilterText}
                onChange={(e) => setCatFilterText(e.target.value)}
                placeholder="Filter categories..."
                className="w-full px-3 py-1.5 border border-[#e7e3dd] rounded-lg text-[10px] mb-2 focus:outline-none focus:border-[#2f6b4f]"
                style={{ fontSize: '16px', touchAction: 'manipulation' }}
              />
              <div className="space-y-1 max-h-48 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
                {categories.filter(c => !catFilterText || c.name.toLowerCase().includes(catFilterText.toLowerCase())).map((cat) => (
                  <button
                    key={cat._id}
                    onClick={() => updateFilter('category', currentCategory === cat.name ? '' : cat.name)}
                    className={`block w-full text-left text-sm py-1 px-2 rounded transition-colors ${currentCategory === cat.name ? 'bg-[#e7f1ea] text-[#2f6b4f] font-medium' : 'text-[#6f6a63] hover:text-[#2d2d2d]'}`}
                  >
                    {cat.name} ({cat.product_count})
                  </button>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Color</h4>
              <input
                type="text"
                value={colorFilterText}
                onChange={(e) => setColorFilterText(e.target.value)}
                placeholder="Filter colors..."
                className="w-full px-3 py-1.5 border border-[#e7e3dd] rounded-lg text-[10px] mb-2 focus:outline-none focus:border-[#2f6b4f]"
                style={{ fontSize: '16px', touchAction: 'manipulation' }}
              />
              <div className="space-y-1 max-h-40 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', touchAction: 'pan-y' }}>
                {colors.filter(c => !colorFilterText || c.toLowerCase().includes(colorFilterText.toLowerCase())).map((color) => (
                  <button
                    key={color}
                    onClick={() => updateFilter('color', currentColor === color ? '' : color)}
                    className={`block w-full text-left text-sm py-1 px-2 rounded transition-colors ${currentColor === color ? 'bg-[#e7f1ea] text-[#2f6b4f] font-medium' : 'text-[#6f6a63] hover:text-[#2d2d2d]'}`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Special Filters */}
            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Availability</h4>
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm text-[#6f6a63] cursor-pointer">
                  <input type="checkbox" checked={isWeeklySpecial} onChange={(e) => updateFilter('weekly_special', e.target.checked ? 'true' : '')} className="rounded" />
                  Weekly Specials
                </label>
                <label className="flex items-center gap-2 text-sm text-[#6f6a63] cursor-pointer">
                  <input type="checkbox" checked={isPrebookFilter} onChange={(e) => updateFilter('prebook', e.target.checked ? 'true' : '')} className="rounded" />
                  Prebook Eligible
                </label>
              </div>
            </div>

            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-xs text-red-600 hover:text-red-700 underline">Clear all filters</button>
            )}
          </div>
        </aside>

        {/* Products Grid */}
        <div className="flex-1 min-w-0">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <h1 className={`text-lg md:text-xl font-semibold tracking-wider uppercase ${isPrebook ? 'animate-pulse-subtle' : ''}`}>
                {currentCategory || (isWeeklySpecial ? 'Weekly Specials' : pageTitle)}
              </h1>
              <span className="text-sm text-[#6f6a63]">({total} products)</span>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="shop-filters-open-button"
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center gap-1.5 px-3 py-2 border border-[#e7e3dd] rounded-lg text-sm"
              >
                <SlidersHorizontal size={14} /> Filters
              </button>
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 mb-4">
              {currentCategory && (
                <span className="flex items-center gap-1 bg-[#e7f1ea] text-[#2f6b4f] text-xs px-3 py-1 rounded-full">
                  {currentCategory} <X size={12} className="cursor-pointer" onClick={() => updateFilter('category', '')} />
                </span>
              )}
              {currentColor && (
                <span className="flex items-center gap-1 bg-[#e7f1ea] text-[#2f6b4f] text-xs px-3 py-1 rounded-full">
                  {currentColor} <X size={12} className="cursor-pointer" onClick={() => updateFilter('color', '')} />
                </span>
              )}
              {currentSearch && (
                <span className="flex items-center gap-1 bg-[#e7f1ea] text-[#2f6b4f] text-xs px-3 py-1 rounded-full">
                  "{currentSearch}" <X size={12} className="cursor-pointer" onClick={() => updateFilter('search', '')} />
                </span>
              )}
            </div>
          )}

          {/* Products Grid - Large photos */}
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="animate-pulse bg-[#fbfaf8] rounded-xl aspect-[3/4]" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#6f6a63] mb-4">No products found matching your criteria.</p>
              <button onClick={clearFilters} className="text-[#2f6b4f] underline text-sm">Clear filters</button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {products.map((product, idx) => {
                  const img = product.images?.[0] || '';
                  return (
                    <button
                      type="button"
                      key={product._id}
                      data-testid={`product-card-${product._id}`}
                      className="group w-full cursor-pointer text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2f6b4f]"
                      onClick={() => openModal(product, idx)}
                      aria-label={`View ${product.name}`}
                    >
                      <div className="relative aspect-[3/4] overflow-hidden rounded-lg bg-[#f5f4f2] mb-2">
                        {img ? (
                          <img
                            src={img}
                            alt={product.name}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#c0c0c0] text-xs">No image</div>
                        )}
                      </div>
                      <h3 className="text-xs font-medium text-[#2d2d2d] uppercase tracking-wide leading-tight mb-1">{product.name}</h3>
                      <div className="w-8 h-0.5 bg-[#2f6b4f]" />
                    </button>
                  );
                })}
              </div>

              {/* Pagination */}
              {total > 24 && (
                <div className="flex justify-center gap-2 mt-8">
                  {Array.from({ length: Math.ceil(total / 24) }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => updateFilter('page', String(page))}
                      className={`w-8 h-8 rounded text-sm ${currentPage === page ? 'bg-[#2d2d2d] text-white' : 'border border-[#e7e3dd] hover:bg-gray-50'}`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Mobile Filters Overlay */}
      {showFilters && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setShowFilters(false)}>
          <div className="absolute right-0 top-0 bottom-0 w-72 bg-white p-6 overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-sm font-semibold tracking-wider uppercase">Filters</h3>
              <button onClick={() => setShowFilters(false)}><X size={20} /></button>
            </div>
            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Category</h4>
              {categories.map((cat) => (
                <button key={cat._id} onClick={() => { updateFilter('category', currentCategory === cat.name ? '' : cat.name); setShowFilters(false); }} className={`block w-full text-left text-sm py-1.5 ${currentCategory === cat.name ? 'text-[#2f6b4f] font-medium' : 'text-[#6f6a63]'}`}>
                  {cat.name}
                </button>
              ))}
            </div>
            <div className="mb-6">
              <h4 className="text-xs font-medium tracking-wider uppercase text-[#6f6a63] mb-2">Color</h4>
              {colors.map((color) => (
                <button key={color} onClick={() => { updateFilter('color', currentColor === color ? '' : color); setShowFilters(false); }} className={`block w-full text-left text-sm py-1.5 ${currentColor === color ? 'text-[#2f6b4f] font-medium' : 'text-[#6f6a63]'}`}>
                  {color}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Product Detail Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center" onClick={closeModal}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 flex overflow-hidden max-h-[85vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Left - Image */}
            <div className="w-1/2 bg-[#f5f4f2] flex-shrink-0">
              {selectedItem.images?.[0] ? (
                <img src={selectedItem.images[0]} alt={selectedItem.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#c0c0c0]">No image</div>
              )}
            </div>

            {/* Right - Info */}
            <div className="w-1/2 p-8 flex flex-col justify-center relative">
              <button
                data-testid="modal-close"
                onClick={closeModal}
                className="absolute top-4 right-4 p-1.5 hover:bg-[#fbfaf8] rounded-lg transition-colors"
              >
                <X size={18} className="text-[#6f6a63]" />
              </button>

              {selectedItem.category && (
                <span className="text-[10px] tracking-wider uppercase text-[#6f6a63] mb-2">{selectedItem.category}</span>
              )}
              <h2 className="text-xl font-semibold tracking-wider uppercase text-[#2d2d2d] mb-3">{selectedItem.name}</h2>
              {selectedItem.color && (
                <p className="text-xs text-[#6f6a63] mb-2">Color: {selectedItem.color}</p>
              )}
              {selectedItem.pack_size && (
                <p className="text-xs text-[#6f6a63] mb-1">Size: {selectedItem.pack_size}</p>
              )}
              {selectedItem.stem_length && (
                <p className="text-xs text-[#6f6a63] mb-1">Stem Length: {selectedItem.stem_length}</p>
              )}
              {selectedItem.origin && (
                <p className="text-xs text-[#6f6a63] mb-1">Origin: {selectedItem.origin}</p>
              )}
              {selectedItem.description && (
                <p className="text-sm text-[#6f6a63] leading-relaxed mt-2">{selectedItem.description}</p>
              )}

              {/* View Product & Buy */}
              <Link
                to={`/product/${selectedItem.slug}`}
                data-testid="modal-view-product"
                className="mt-5 flex items-center justify-center gap-2 bg-[#2d2d2d] text-white py-2.5 px-6 rounded-lg text-sm font-medium hover:bg-[#232323] transition-colors w-full"
                onClick={closeModal}
              >
                View Product Details
              </Link>

              {/* Navigation */}
              <div className="flex items-center gap-3 mt-auto pt-8">
                <button
                  data-testid="modal-prev"
                  onClick={goPrev}
                  disabled={selectedIndex <= 0}
                  className="flex items-center gap-1 text-xs text-[#6f6a63] hover:text-[#2d2d2d] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <span className="text-[10px] text-[#c0c0c0] flex-1 text-center">{selectedIndex + 1} / {products.length}</span>
                <button
                  data-testid="modal-next"
                  onClick={goNext}
                  disabled={selectedIndex >= products.length - 1}
                  className="flex items-center gap-1 text-xs text-[#6f6a63] hover:text-[#2d2d2d] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
